<template>
    <Dialog :open="isOpen" @update:open="handleOpenChange">
        <DialogContent class="max-h-90-dynamic flex flex-col !max-w-4xl w-full">
            <DialogHeader>
                <DialogTitle class="flex items-center gap-2">
                    <AlertTriangle class="h-5 w-5 text-destructive" />
                    Error Details
                </DialogTitle>
                <DialogDescription class="sr-only"> </DialogDescription>
            </DialogHeader>

            <div class="flex-1 overflow-hidden flex flex-col min-h-0">
                <div class="mb-4">
                    <p class="text-sm text-muted-foreground">The following {{ (errors?.length || 0) > 1 ? 'errors' : 'error' }} occurred:</p>
                </div>

                <!-- Pagination for multiple errors -->
                <div v-if="(errors?.length || 0) > 1" class="mb-4">
                    <div class="flex items-center justify-between text-sm text-muted-foreground">
                        <span>
                            Showing error <span class="font-medium">{{ currentPage + 1 }}</span> out of
                            <span class="font-medium">{{ errors?.length || 0 }}</span>
                        </span>
                        <div class="flex gap-2">
                            <Button @click="previousError" :disabled="currentPage === 0" variant="outline" size="sm">
                                <ChevronLeft class="h-4 w-4" />
                                Previous
                            </Button>
                            <Button @click="nextError" :disabled="currentPage === (errors?.length || 1) - 1" variant="outline" size="sm">
                                Next
                                <ChevronRight class="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                <!-- Enhanced Error display -->
                <div class="flex-1 min-h-0 border rounded-lg overflow-hidden">
                    <div class="bg-muted/50 px-3 py-2 border-b flex justify-between items-center">
                        <div class="flex items-center gap-3">
                            <span class="text-sm font-medium">{{ (errors?.length || 0) > 1 ? `Error ${currentPage + 1}` : 'Error Details' }}</span>
                            <Badge v-if="detectedErrorType !== 'Text'" variant="outline" class="text-xs">
                                {{ detectedErrorType }}
                            </Badge>
                            <!-- Search input for long errors -->
                            <div v-if="isLongError" class="flex items-center gap-1 text-xs">
                                <Search class="h-3 w-3 text-muted-foreground" />
                                <input
                                    v-model="searchTerm"
                                    placeholder="Search..."
                                    class="w-32 px-2 py-1 text-xs border rounded bg-background"
                                    aria-label="Search error text"
                                    aria-describedby="search-results" />
                                <span
                                    v-if="searchTerm && searchMatches"
                                    id="search-results"
                                    class="text-muted-foreground min-w-20"
                                    aria-live="polite">
                                    {{ searchMatches }}
                                </span>
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                            <Button @click="copyToClipboard" :disabled="copied" variant="ghost" size="sm">
                                <component :is="copied ? Check : Copy" class="h-4 w-4 mr-1" />
                                {{ copied ? 'Copied!' : 'Copy Error' }}
                            </Button>
                        </div>
                    </div>
                    <div class="bg-muted p-4 overflow-auto max-h-96">
                        <!-- Enhanced error display based on type -->
                        <component :is="errorDisplayComponent" :message="processedError" :search-term="searchTerm" @toggle-section="toggleSection" />
                    </div>
                </div>
            </div>

            <DialogFooter class="flex-shrink-0">
                <Button @click="openChat" variant="outline"> Contact Support </Button>
                <Button @click="onClose"> Close </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { AlertTriangle, Copy, Check, ChevronLeft, ChevronRight, Search } from 'lucide-vue-next';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StackTraceDisplay from './error-display/StackTraceDisplay.vue';
import JsonErrorDisplay from './error-display/JsonErrorDisplay.vue';
import SimpleErrorDisplay from './error-display/SimpleErrorDisplay.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Array,
        required: true
    },
    width: {
        type: Number,
        default: 65
    }
});

const emit = defineEmits(['update:open']);

const currentPage = ref(0);
const copied = ref(false);
const searchTerm = ref('');
const collapsedSections = ref(new Set());

const currentError = computed(() => props.errors?.[currentPage.value] || '');

const formattedError = computed(() => {
    const parts = currentError.value?.split('\nYou can also find') ?? [];
    return parts.length === 2 ? parts[0] : (currentError.value ?? '');
});

// Enhanced error type detection
const detectedErrorType = computed(() => {
    const error = formattedError.value;

    if (isComplexError(error)) return 'Complex';
    if (isStackTrace(error)) return 'Stack Trace';
    if (isJsonError(error)) return 'JSON';
    if (isValidationError(error)) return 'Validation';
    if (isSqlError(error)) return 'SQL';

    return 'Text';
});

const isLongError = computed(() => {
    return formattedError.value.length > 500 || formattedError.value.split('\n').length > 10;
});

const searchMatches = computed(() => {
    if (!searchTerm.value) return null;
    const matches = (formattedError.value.match(new RegExp(searchTerm.value, 'gi')) || []).length;
    return matches > 0 ? `${matches} match${matches !== 1 ? 'es' : ''}` : 'No matches';
});

// Process error for enhanced display
const processedError = computed(() => {
    const error = formattedError.value;
    const type = detectedErrorType.value;

    // Always split by newlines for plain text processing
    // SimpleErrorDisplay will handle the formatting and apply color enhancements
    const lines = error.split('\n');

    return {
        raw: error,
        type,
        lines: lines,
        searchTerm: searchTerm.value,
        collapsedSections: collapsedSections.value
    };
});

// Dynamic component selection based on error type
const errorDisplayComponent = computed(() => {
    switch (detectedErrorType.value) {
        case 'Stack Trace':
            return StackTraceDisplay;
        case 'Complex':
        case 'JSON':
        case 'Validation':
            return JsonErrorDisplay;
        default:
            return SimpleErrorDisplay;
    }
});

// Error type detection functions
function isComplexError(error) {
    // Detect complex error messages with mixed formats and structures
    const complexPatterns = [
        // Messages starting with a prefix followed by JSON arrays
        /^[^:]+:\s*\[.*".*\\n.*".*\]/s,

        // Any JSON array containing strings with error patterns
        /\[.*".*(?:at\s+|Exception|Error:|\.java:|\.scala:|\.py:|\.js:|\.ts:|\.go:|\.rs:|\.cpp:|\.c\b).*\\n.*".*\]/s,

        // Messages with failure keywords + JSON structure
        /(?:failed|error|exception|crashed|timeout|denied).*\[.*".*(?:ERROR|WARN|INFO|DEBUG|FATAL).*\\n.*".*\]/is,

        // Arrays containing logging output with timestamps
        /\[.*".*\d{2}:\d{2}:\d{2}.*(?:ERROR|Exception).*\\n.*".*\]/s,

        // Mixed format: prefix + array with stack traces
        /^.+?:\s*\[.*".*(?:Caused by|at\s+\w+\.|Exception in thread).*\\n.*".*\]/s,

        // Arrays with escaped newlines containing class/method references
        /\[.*".*\\n.*(?:at\s+[\w.]+\(|org\.|com\.|java\.|scala\.|Exception:|Error:).*".*\]/s,

        // Complex deployment/build error structures
        /(?:deploy|build|compilation|execution).*failed.*\[.*".*\\n.*".*\]/is,

        // Generic: any bracketed content with multiple quoted strings containing newlines and error indicators
        /\[(?:[^"]*"[^"]*\\n[^"]*(?:error|exception|fail|at\s+|caused by)[^"]*"[^"]*,?\s*){2,}\]/is,

        // Mixed content: structured data + unescaped content
        /^[^[]*\[.*".*\\n.*".*\].*[\n\r].*(?:at\s+|Exception|Error:|Caused by)/s,

        // Generic escaped content pattern
        /\[.*".*\\[nt].*(?:ERROR|WARN|Exception|at\s+|Caused by|\.java:|\.scala:|\.py:).*\\[nt].*".*\]/s
    ];

    // Also check for structural complexity indicators
    const structuralComplexity =
        // Contains both JSON array structure AND stack trace patterns
        error.includes('[') &&
        error.includes('"') &&
        error.includes('\\n') &&
        /at\s+[\w.]+\(|Exception:|Error:|Caused by:/i.test(error) &&
        // Has multiple error-like strings
        (error.match(/"[^"]*(?:ERROR|WARN|Exception|at\s+)[^"]*"/g) || []).length >= 2;

    return complexPatterns.some((pattern) => pattern.test(error)) || structuralComplexity;
}

function isStackTrace(error) {
    const stackTracePatterns = [
        /at .+\(.+:\d+:\d+\)/, // Node.js/V8 stack traces
        /\s+at .+\(.+\.\w+:\d+\)/, // General stack trace pattern
        /\w+Error: .+\n\s+at/, // Error with stack trace
        /File ".+", line \d+/, // Python-style stack traces
        /\tat .+\.\w+\(.+\.\w+:\d+\)/, // Java-style stack traces
        /\n\s{2,}at\s/ // Indented "at" lines
    ];
    return stackTracePatterns.some((pattern) => pattern.test(error));
}

function isJsonError(error) {
    try {
        JSON.parse(error);
        return true;
    } catch {
        return error.includes('{') && error.includes('}') && (error.includes('"') || error.includes("'"));
    }
}

function isValidationError(error) {
    const validationPatterns = [
        /validation error/i,
        /field .+ is required/i,
        /invalid .+ format/i,
        /must be .+ characters/i,
        /does not match pattern/i
    ];
    return validationPatterns.some((pattern) => pattern.test(error));
}

function isSqlError(error) {
    const sqlPatterns = [/sql/i, /database/i, /query/i, /table .+ doesn't exist/i, /column .+ not found/i, /syntax error/i];
    return sqlPatterns.some((pattern) => pattern.test(error));
}

const nextError = () => {
    if (currentPage.value < (props.errors?.length || 1) - 1) {
        currentPage.value++;
    }
};

const previousError = () => {
    if (currentPage.value > 0) {
        currentPage.value--;
    }
};

const copyToClipboard = async () => {
    copied.value = true;
    await navigator.clipboard.writeText(formattedError.value);
    setTimeout(() => {
        copied.value = false;
    }, 3000);
};

const toggleSection = (sectionId) => {
    if (collapsedSections.value.has(sectionId)) {
        collapsedSections.value.delete(sectionId);
    } else {
        collapsedSections.value.add(sectionId);
    }
    // Trigger reactivity
    collapsedSections.value = new Set(collapsedSections.value);
};

const openChat = () => {
    if (window.Pylon) {
        window.Pylon('show');
    }
};

const handleOpenChange = (open) => {
    if (!open) {
        onClose();
    }
};

const onClose = () => {
    emit('update:open', false);
    currentPage.value = 0;
    copied.value = false;
    searchTerm.value = '';
    collapsedSections.value = new Set();
};
</script>
